import { FC, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Card, CardContent, Combobox, Datepicker } from '@ibsys/kit';
import type { ConversationAttributeItem } from '@/entities/attributes/model/api/types';
import type {
  AttributeFilter,
  DashboardBlockId,
  DashboardQualityDisplaySettings,
  HeatmapMode,
  HiddenCriterionRef,
} from '@/entities/dashboards';
import type { QualityZoneRanges } from '@/shared/lib';
import {
  AttributeFilterCell,
  AttributeFilterPicker,
  createEmptyFilter,
  type AttributeFilterPickerField,
} from '@/features/attribute-filters';
import { ConsultantFilterCell } from './ConsultantFilterCell';
import { DisplaySettings } from './DisplaySettings';

/** Код фильтра «Консультант» среди основных полей диалога в перечне атрибутных фильтров. */
const CONSULTANT_FIELD_CODE = 'consultant';

/**
 * Базовые фильтры дашборда качества (единый объект-state на странице).
 *
 * `dateFrom`/`dateTo` — границы периода в формате YYYY-MM-DD; пустая строка означает,
 * что граница не задана и не передаётся в запрос. Обе границы сравниваются с
 * `conversation.started_at`.
 */
export type DashboardFiltersState = {
  consultant: string;
  dateFrom: string;
  dateTo: string;
  heatmapMode: HeatmapMode;
};

type Props = {
  /**
   * Показан пользовательский дашборд — применённый или правящийся: в панели только период,
   * атрибутные фильтры и сброс
   */
  isCustomDashboard: boolean;
  consultantNames: string[];
  /** Единый объект-state базовых фильтров */
  filters: DashboardFiltersState;
  /** Единый колбек обновления всего объекта фильтров */
  onChangeFilters: (next: DashboardFiltersState) => void;
  /** true — нужно показать кнопку «Сбросить drill-down» */
  hasDrill: boolean;
  /** Доступные атрибуты для добавления в панель фильтров (системная конфигурация) */
  availableAttributes: ConversationAttributeItem[];
  /** Активные атрибутные фильтры */
  activeAttributeFilters: AttributeFilter[];
  onChangeAttributeFilters: (filters: AttributeFilter[]) => void;
  /** Консультанты фильтра по атрибуту пользовательского дашборда; `undefined` — фильтра на панели нет */
  employeeIds?: number[];
  /**
   * Смена консультантов фильтра по атрибуту (`undefined` убирает фильтр); не задана — «Консультант»
   * в перечне фильтров не предлагается
   */
  onChangeEmployeeIds?: (employeeIds: number[] | undefined) => void;
  onReset: () => void;
  onClearDrill: () => void;
  /** Пользовательские пороги зон мини-отчёта (настройка отображения). */
  zoneRanges: QualityZoneRanges;
  /** Критерии, скрытые из отображения (настройка отображения), — каждый со своим заключением. */
  hiddenCriteria: HiddenCriterionRef[];
  /** Идентификаторы скрытых блоков дашборда (настройка отображения). */
  hiddenBlocks: DashboardBlockId[];
  /** Колбэк изменения настроек отображения (полным объектом). */
  onChangeDisplay: (next: DashboardQualityDisplaySettings) => void;
};

/**
 * Панель фильтров дашборда качества.
 * Базовые фильтры (консультант, «Дата от», «Дата до», режим heatmap)
 * и атрибутные фильтры располагаются в одной общей сетке. Лейблом атрибутного
 * поля выступает имя атрибута, тип компонента выбирается по `Attribute.dataType`.
 *
 * «Дата от»/«Дата до» — независимые границы по полю самого разговора: обе сравниваются
 * с `conversation.started_at` — той же колонкой, по которой группируется ячейка тепловой
 * карты, иначе ячейка и её детализация расходятся. Параллельно остаётся доступным
 * атрибутный фильтр на DATE/TIMESTAMP-атрибуте — оба механизма работают через AND.
 *
 * У пользовательского дашборда фильтры — только отбор данных его диаграмм: период и атрибуты.
 * Отдельный выбор консультанта, режим тепловой карты, сброс разбора и настройки отображения относятся
 * к разделам стандартного дашборда, которых на пользовательском нет, — там их и не показываем.
 * Консультант на пользовательском дашборде добавляется из перечня атрибутных фильтров.
 */
export const QualityFilters: FC<Props> = ({
  isCustomDashboard,
  consultantNames,
  filters,
  onChangeFilters,
  hasDrill,
  availableAttributes,
  activeAttributeFilters,
  onChangeAttributeFilters,
  employeeIds,
  onChangeEmployeeIds,
  onReset,
  onClearDrill,
  zoneRanges,
  hiddenCriteria,
  hiddenBlocks,
  onChangeDisplay,
}) => {
  const { t } = useTranslation('qualityDashboard');

  const setField = <K extends keyof DashboardFiltersState>(key: K, value: DashboardFiltersState[K]) =>
    onChangeFilters({ ...filters, [key]: value });

  const consultantOptions = useMemo(
    () => [{ label: t('filters.all'), value: '' }, ...consultantNames.map((n) => ({ label: n, value: n }))],
    [consultantNames, t],
  );

  const heatmapOptions = useMemo(
    () => [
      { label: t('filters.byMonth'), value: 'month' as const },
      { label: t('filters.byWeek'), value: 'week' as const },
    ],
    [t],
  );

  // Карта атрибутов по `path` — единый ключ совпадает с `path` в attribute-filters
  const attributesByPath = useMemo(() => {
    const map = new Map<string, ConversationAttributeItem>();
    availableAttributes.forEach((a) => map.set(a.path, a));
    return map;
  }, [availableAttributes]);

  const activePaths = useMemo(() => new Set(activeAttributeFilters.map((f) => f.path)), [activeAttributeFilters]);

  const handleAddAttribute = (path: string) => {
    const attribute = attributesByPath.get(path);
    if (!attribute) return;
    const newFilter = createEmptyFilter(attribute);
    if (!newFilter) return;
    onChangeAttributeFilters([...activeAttributeFilters, newFilter]);
  };

  const handleUpdateFilter = (next: AttributeFilter) => {
    onChangeAttributeFilters(activeAttributeFilters.map((f) => (f.path === next.path ? next : f)));
  };

  const handleRemoveFilter = (path: string) => {
    onChangeAttributeFilters(activeAttributeFilters.filter((f) => f.path !== path));
  };

  // Консультант — основное поле диалога, а не атрибут: в перечне он стоит наравне с атрибутами, пока
  // его фильтра на панели нет
  const pickerFields = useMemo<AttributeFilterPickerField[]>(
    () =>
      onChangeEmployeeIds && employeeIds === undefined
        ? [{ code: CONSULTANT_FIELD_CODE, label: t('filters.consultant') }]
        : [],
    [onChangeEmployeeIds, employeeIds, t],
  );

  const handleAddField = (code: string) => {
    if (code === CONSULTANT_FIELD_CODE) onChangeEmployeeIds?.([]);
  };

  return (
    <Card>
      <CardContent className="p-4 flex flex-col gap-4">
        <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-3 items-end">
          {!isCustomDashboard && (
            <Combobox
              label={t('filters.consultant')}
              value={filters.consultant}
              onChange={(v) => setField('consultant', String(v ?? ''))}
              options={consultantOptions}
              isSearchable
              className="w-full"
            />
          )}
          <Datepicker
            mode="single"
            outputType="iso-date"
            label={t('filters.dateFrom')}
            value={filters.dateFrom}
            onChange={(v) => setField('dateFrom', typeof v === 'string' ? v : '')}
          />
          <Datepicker
            mode="single"
            outputType="iso-date"
            label={t('filters.dateTo')}
            value={filters.dateTo}
            onChange={(v) => setField('dateTo', typeof v === 'string' ? v : '')}
          />
          {!isCustomDashboard && (
            <Combobox
              label={t('filters.heatmapMode')}
              value={filters.heatmapMode}
              onChange={(v) => setField('heatmapMode', (v as HeatmapMode) ?? 'month')}
              options={heatmapOptions}
              isSearchable={false}
              className="w-full"
            />
          )}
          {onChangeEmployeeIds && employeeIds !== undefined && (
            <ConsultantFilterCell
              employeeIds={employeeIds}
              onChange={onChangeEmployeeIds}
              onRemove={() => onChangeEmployeeIds(undefined)}
            />
          )}
          {activeAttributeFilters.map((filter) => (
            <AttributeFilterCell
              key={filter.path}
              attribute={attributesByPath.get(filter.path)}
              filter={filter}
              onChange={handleUpdateFilter}
              onRemove={() => handleRemoveFilter(filter.path)}
            />
          ))}
        </div>
        <div className="flex flex-wrap gap-2 justify-between items-center">
          <AttributeFilterPicker
            availableAttributes={availableAttributes}
            activePaths={activePaths}
            onAdd={handleAddAttribute}
            fields={pickerFields}
            onAddField={handleAddField}
          />
          <div className="flex flex-wrap items-center gap-2">
            <Button variant="secondary" size="sm" onClick={onReset}>
              {t('filters.reset')}
            </Button>
            {hasDrill && !isCustomDashboard && (
              <Button variant="secondary" size="sm" onClick={onClearDrill}>
                {t('filters.clearDrill')}
              </Button>
            )}
          </div>
        </div>
        {!isCustomDashboard && (
          <DisplaySettings
            zoneRanges={zoneRanges}
            hiddenCriteria={hiddenCriteria}
            hiddenBlocks={hiddenBlocks}
            onChange={onChangeDisplay}
          />
        )}
      </CardContent>
    </Card>
  );
};
